/**
 * 官媒舱创建页(R2-T4,照 dept-reporter-create.html):
 * 左 = 来源条(体裁)+ ① 原始素材(语气 / 字数 / 产品归属)+ ② AI 整理回显(三色分级表);
 * 右 = 写作规划 cockpit(确认后才生成)。
 *
 * ═══ 流程与后端契约 ═══════════════════════════════════════════════════════════
 * 建稿是**懒**的:点「整理素材」才 createDraft(纯落库)→ startTask(MATERIAL_EXTRACT);
 * 之后 PLAN(确定性零 LLM)→ GENERATE(草稿状态机 CAS)。整理/规划可反复跑。
 * 轮询主口 = GET /drafts/{id} 聚合详情;**字段名就是后端的 `id`**,不造 draftId 二名
 * (视频线「草稿字段 draftId 非 id」的探针坑,这里从源头不犯)。
 *
 * ★体裁 / 产品 / 子类建稿后锁定(后端 UpdateDraftRequest 只有 tone/targetWords/materialRaw):
 *   「换体裁」= 弃当前草稿号重新开一篇,UI 如实说,不做假的原地切换。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Newspaper, RefreshCw, Wand2 } from "lucide-react";

import { BlueprintHeroCard } from "@/entities/explosive";
import { HandoffPreview } from "@/entities/explosive";
import { ReferenceContentPanel } from "@/entities/explosive";
import { buildReferenceContent } from "@/entities/explosive";

import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/PageHeader";
import { SourceStrip } from "@/shared/ui/SourceStrip";
import { StepIndicator } from "@/shared/ui/StepIndicator";
import { Textarea } from "@/shared/ui/textarea";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { cn } from "@/shared/lib/utils";
import { productService } from "@/entities/product";
import { pickableProducts } from "@/features/poster/model/free-create/productAdapter";
import {
  clearSceneCardState,
  parseScenePreset,
  type ReporterMaterialSeed,
} from "@/entities/scene-card/model/presets";
import {
  reporterService,
  type ReporterDraftDetailDto,
  type ReporterGenre,
  type ReporterTone,
} from "@/features/reporter/api/reporterService";
import { REPORTER_CREDITS_EXTRACT_APPROX } from "@/entities/scene-card/model/creditRates";
// 2026-10-10 追爆原话复用 §4:C1 逐句 + C3 borrow(entities 层直连文件,不走 barrel)
import { blueprintLinesService } from "@/entities/explosive/api/blueprintLinesService";
import {
  BORROW_MODE_META,
  borrowCraftOf,
  buildReporterBorrow,
  defaultAttribution,
  defaultBorrowPicks,
  genreForBorrow,
  parseReporterBorrow,
  splitLegacyCraft,
  type BorrowMode,
  type ReporterBorrow,
} from "@/entities/explosive/model/reporterBorrow";
import { parseMaterial, parsePlan } from "@/features/reporter/model/parse";
import { rememberSiteTarget } from "@/features/reporter/model/sitePublication";
import {
  parseKeywords,
  serializeKeywords,
  type KeywordItem,
} from "@/features/reporter/model/keywords";
import {
  BRAND_SUBTYPES,
  normalizeBrandSubType,
  GENRE_LABEL,
  TONE_LABEL,
  TONE_ORDER,
} from "@/features/reporter/model/labels";
import { BlueprintCraftGrid } from "@/features/reporter/ui/BlueprintCraftGrid";
import { BorrowPicker } from "@/features/reporter/ui/BorrowPicker";
import { KeywordZone } from "@/features/reporter/ui/KeywordZone";
import { MaterialTable } from "@/features/reporter/ui/MaterialTable";
import { PlanPanel } from "@/features/reporter/ui/PlanPanel";

const STEPS = ["素材", "规划确认", "成稿"] as const;

/**
 * BPWB-2(2026-09-03):追爆来的稿,素材框整个还给用户 —— 写法四行已经搬到蓝图工作卡的
 * 展示插槽,这里只留一句引导,不预填任何值(placeholder,不是初始值)。
 */
const BLUEPRINT_MATERIAL_PLACEHOLDER =
  "你的业务是什么、这篇文章要讲哪个点、有哪些真实数字和案例。\n参考片的选题是别人家的 —— 这里写你自己的。";
const DEFAULT_MATERIAL_PLACEHOLDER =
  "把手里的素材原样粘进来:事件经过、产品资料、活动信息、口头转述都行 —— AI 先整理再动笔,素材里没有的不会编。";

/** S2 素材种子 → 素材框预填文本(进框可编辑,不静默直发)。
 * R2:关键词不再拼进文本降级 —— 结构化直落关键词区(seedKeywords)。 */
function seedToText(seed: ReporterMaterialSeed): string {
  const lines: string[] = [];
  if (seed.title) lines.push(`主题:${seed.title}`);
  if (seed.userQuestion) lines.push(`要回答的问题:${seed.userQuestion}`);
  if (seed.channel) lines.push(`发布渠道:${seed.channel}`);
  if (seed.audience) lines.push(`目标受众:${seed.audience}`);
  // 自由文本正文(追爆拆解产出等)排结构化字段之后,空行隔开
  if (seed.body) lines.push("", seed.body);
  return lines.join("\n");
}

/** GEO 雷达/追爆种子关键词 → 结构化关键词条目(source=geo_radar,默认选用)。 */
function seedKeywords(seed: ReporterMaterialSeed | undefined): KeywordItem[] {
  if (!seed?.keywords?.length) return [];
  const seen = new Set<string>();
  return seed.keywords
    .map((w) => w.trim())
    .filter((w) => w && !seen.has(w) && (seen.add(w), true))
    .map((word) => ({
      word,
      group: "industry" as const,
      source: "geo_radar" as const,
      selected: true,
    }));
}

const RUNNING = "RUNNING";
const latestTask = (d: ReporterDraftDetailDto | undefined, type: string) =>
  d?.recentTasks.find((t) => t.type === type);

export default function ReporterCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();

  // 1009:建站舱「新闻栏 → 新写一篇」带 ?site=,记下来给预览页的「发布到官网」预选
  const siteTarget = sp.get("site");
  useEffect(() => rememberSiteTarget(siteTarget), [siteTarget]);

  // 预设一次性消费(卡片 → 创建页;刷新不重放)
  const preset = useRef(parseScenePreset(location.state)).current;
  const reporterPreset = preset?.kind === "reporter" ? preset : null;
  useEffect(() => {
    if (preset) clearSceneCardState();
  }, [preset]);

  /* ── 输入面 ── */
  const [genre, setGenre] = useState<ReporterGenre | null>(reporterPreset?.genre ?? null);
  const [subType, setSubType] = useState<string>(normalizeBrandSubType(reporterPreset?.subType));
  const [tone, setTone] = useState<ReporterTone>("PROFESSIONAL");
  const [targetWords, setTargetWords] = useState(1200);
  const [materialRaw, setMaterialRaw] = useState(
    reporterPreset?.materialSeed ? seedToText(reporterPreset.materialSeed) : ""
  );
  const [productCode, setProductCode] = useState("");
  const [keywords, setKeywords] = useState<KeywordItem[]>(() =>
    seedKeywords(reporterPreset?.materialSeed)
  );
  const [useKnowledge, setUseKnowledge] = useState(true);

  /* ── 2026-10-10 §4「怎么借这条爆款」(追爆来的稿才有) ── */
  const blueprintId = reporterPreset?.formulaName ? (reporterPreset.blueprintId ?? null) : null;
  const linesQ = useQuery({
    queryKey: ["blueprint-lines", blueprintId],
    queryFn: () => blueprintLinesService.analyze(blueprintId!),
    enabled: blueprintId != null,
    retry: false,
    staleTime: Infinity,
  });
  const borrowLines = useMemo(() => linesQ.data?.lines ?? [], [linesQ.data]);
  const borrowLinesStatus: "loading" | "ready" | "unavailable" =
    blueprintId == null
      ? "unavailable"
      : linesQ.isLoading
        ? "loading"
        : borrowLines.length > 0
          ? "ready"
          : "unavailable";
  // 默认「仿它的说法」(与数字人默认 A 同精神:原话尽量用上);逐句拿不到只给 STRUCTURE
  const [borrowMode, setBorrowMode] = useState<BorrowMode>("STYLE");
  // 勾选以句子原文记(草稿回显按文本对);null = 还没动过 → 默认勾钩子句
  const [quotePicks, setQuotePicks] = useState<string[] | null>(null);
  const [stylePicks, setStylePicks] = useState<string[] | null>(null);
  const defaultAttr = defaultAttribution(reporterPreset?.sourcePlatform);
  const [attribution, setAttribution] = useState(defaultAttr);
  // GET 回来的 borrow(编辑已有草稿):回显 + 没有蓝图上下文时原样回送(不把它冲掉)
  const [hydratedBorrow, setHydratedBorrow] = useState<ReporterBorrow | null>(null);
  // 用户亲手「换体裁」后,借法不再联动体裁
  const genreTouched = useRef(false);

  /* ── 舱级元信息:租户行业 + GEO 政策(合规提示条;取不到不拦流程) ── */
  const metaQ = useQuery({
    queryKey: ["reporter-meta"],
    queryFn: () => reporterService.getMeta(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  const geoPolicy = metaQ.data?.geoPolicy?.toLowerCase() ?? null;

  const draftIdParam = sp.get("draftId");
  const draftId = draftIdParam ? Number(draftIdParam) : null;

  /* ── 轮询主口:草稿聚合详情(有在途任务/生成中才转) ── */
  const draftQ = useQuery({
    queryKey: ["reporter-draft", draftId],
    queryFn: () => reporterService.getDraft(draftId!),
    enabled: draftId != null && Number.isFinite(draftId),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      const busy = d.status === "GENERATING" || d.recentTasks.some((t) => t.status === RUNNING);
      return busy ? 2500 : false;
    },
  });
  const draft = draftQ.data;

  // 恢复已有草稿:输入面回显后端真值(仅首拉;之后以本地编辑为准)
  const hydrated = useRef(false);
  useEffect(() => {
    if (!draft || hydrated.current) return;
    hydrated.current = true;
    setGenre(draft.genre);
    setSubType(normalizeBrandSubType(draft.subType));
    setTone(draft.tone ?? "PROFESSIONAL");
    if (draft.targetWords) setTargetWords(draft.targetWords);
    // 2026-10-10:materialRaw 只放用户自己写的(写法/原话走 borrow),原样回显
    if (draft.materialRaw) setMaterialRaw(draft.materialRaw);
    const b = parseReporterBorrow(draft.borrow);
    if (b) {
      setHydratedBorrow(b);
      setBorrowMode(b.mode);
      if (b.quotes?.length) {
        setQuotePicks(b.quotes.map((q) => q.text));
        if (b.quotes[0].attribution) setAttribution(b.quotes[0].attribution);
      }
      if (b.styleLines) setStylePicks(b.styleLines);
    }
    if (draft.productCode) setProductCode(draft.productCode);
    if (draft.useKnowledge != null) setUseKnowledge(draft.useKnowledge);
  }, [draft]);

  // 关键词与服务端同步:KEYWORD_EXPAND 回调把候选合并进 draft.keywordsJson,轮询带回后
  // 覆盖本地(拓词前 ensureDraft 已把本地编辑存进服务端,合并结果以服务端为准不丢手填)。
  const lastServerKeywords = useRef<string | null>(null);
  useEffect(() => {
    const raw = draft?.keywordsJson ?? null;
    if (raw === lastServerKeywords.current) return;
    lastServerKeywords.current = raw;
    if (raw) setKeywords(parseKeywords(raw));
  }, [draft?.keywordsJson]);

  // 已成稿的草稿不归创建页管 —— 直接进工作台(回退到这页也不该重复生成)
  useEffect(() => {
    if (draft && (draft.status === "GENERATED" || draft.status === "EDITED")) {
      navigate(`/ai-jizhe/workbench/${draft.id}`, { replace: true });
    }
  }, [draft, navigate]);

  const material = useMemo(() => parseMaterial(draft?.materialJson), [draft?.materialJson]);
  const plan = useMemo(() => parsePlan(draft?.planJson), [draft?.planJson]);

  const extractTask = latestTask(draft, "MATERIAL_EXTRACT");
  const planTask = latestTask(draft, "PLAN");
  const expandTask = latestTask(draft, "KEYWORD_EXPAND");
  const extracting = extractTask?.status === RUNNING;
  const planning = planTask?.status === RUNNING;
  const expanding = expandTask?.status === RUNNING;
  const generating = draft?.status === "GENERATING";
  // 拓词不进 busy:候选生成期间素材/语气照常可编辑,只锁拓词按钮自身。
  const busy = extracting || planning || generating;

  const step = generating ? 3 : material ? 2 : 1;

  /* ── 产品树(可选归属;与海报同一棵、同一 queryKey) ── */
  const productsQ = useQuery({
    queryKey: ["tenant-products"],
    queryFn: () => productService.list(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const products = pickableProducts(productsQ.data ?? []);

  /* ── 借法派生值 + C3 borrow ── */
  // 逐句拿不到(且没有回显来的借法)→ 只能学结构
  const effectiveBorrowMode: BorrowMode =
    blueprintId != null && borrowLinesStatus === "unavailable" && !hydratedBorrow
      ? "STRUCTURE"
      : borrowMode;
  const effQuotes = quotePicks ?? defaultBorrowPicks(borrowLines);
  const effStyle = stylePicks ?? defaultBorrowPicks(borrowLines);
  const borrowPayload: ReporterBorrow | null =
    blueprintId == null
      ? hydratedBorrow
      : borrowLinesStatus !== "ready" && hydratedBorrow
        ? hydratedBorrow
        : buildReporterBorrow({
            mode: effectiveBorrowMode,
            quotes: effQuotes,
            attribution,
            defaultAttr,
            styleLines: effStyle,
            craft: borrowCraftOf(
              reporterPreset?.blueprintPayload,
              reporterPreset?.blueprintBreakdown
            ),
            lines: borrowLines,
          });
  const onBorrowMode = (m: BorrowMode) => {
    setBorrowMode(m);
    // 体裁跟借法走(CITE→企业新闻;STYLE/STRUCTURE→按用途);建稿后体裁锁定、亲手换过体裁不联动
    if (!genreTouched.current && draftId == null) {
      setGenre(genreForBorrow(m, reporterPreset?.blueprintPayload?.purposes));
    }
  };
  const togglePick = (list: string[], text: string) =>
    list.includes(text) ? list.filter((t) => t !== text) : [...list, text];

  /* ── 动作 ── */
  const invalidate = () => qc.invalidateQueries({ queryKey: ["reporter-draft", draftId] });

  /**
   * 建稿(懒)或同步输入面,返回草稿 id。
   *
   * `materialOverride`:调用方刚算出、还没经过 state 的素材文本(缺失字段就地补充就是这个
   * 场景 —— setMaterialRaw 之后立刻重新整理)。显式传下来,发出去的那份就与 setState 的
   * 刷新时序无关。
   *
   * ★老实说一句:2026-08-14 真去验过「不传会不会发旧素材」—— 单测和真浏览器**都发的是新素材**
   *   (react-query 在 mutationFn 真跑之前已经取到重渲后的新闭包)。所以这个参数是**让正确性
   *   不依赖刷新时序**的护栏,不是在修一个复现得出来的 bug;别照着它去写「已修 XX」。
   */
  const ensureDraft = async (materialOverride?: string): Promise<number> => {
    const keywordsJson = serializeKeywords(keywords);
    // 2026-10-10(修 P5):materialRaw 只放用户自己写的 —— 参考片写法/原话不再拼进来
    // (素材会被 fact_guard 当证据),改走独立字段 borrow(C3)。
    const submitRaw = materialOverride ?? materialRaw;
    const borrow = borrowPayload;
    if (draftId != null && draft) {
      await reporterService.updateDraft(draftId, {
        tone,
        targetWords,
        materialRaw: submitRaw,
        keywordsJson,
        useKnowledge,
        ...(borrow ? { borrow } : {}),
      });
      return draftId;
    }
    const created = await reporterService.createDraft({
      genre: genre!,
      // 子类只对品牌介绍有效;切过体裁残留的子类后端会拒,这里不带。
      subType: genre === "BRAND_PROFILE" && subType ? subType : null,
      tone,
      targetWords,
      materialRaw: submitRaw,
      productCode: productCode || null,
      keywordsJson,
      useKnowledge,
      sourceDraftId: reporterPreset?.sourceDraftId ?? null,
      // 蓝图取用登记(0813):后端据此 +1(只记账,语义不变)。
      explosiveBlueprintId: reporterPreset?.blueprintId ?? null,
      ...(borrow ? { borrow } : {}),
    });
    setSp({ draftId: String(created.id) }, { replace: true });
    return created.id;
  };

  const extractMut = useMutationWithToast({
    mutationFn: async (materialOverride?: string) => {
      const id = await ensureDraft(materialOverride);
      await reporterService.startTask(id, { type: "MATERIAL_EXTRACT" });
      return id;
    },
    errorToast: "整理没发起来",
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["reporter-draft", id] });
    },
  });

  /**
   * 缺失字段就地补充(TOP#4)—— 把用户手填的几条**追加进原始素材**,再整份重新整理。
   *
   * 为什么并进 materialRaw 而不是另存一份「补充字段」:整理是拿素材原文跑的,另存一份就要
   * 后端同时认两个入口(而这一刀说好了不动后端)。追加进素材还有个好处 —— 用户在上面那个
   * 输入框里**看得见自己补了什么**,下次重整、换体裁都带着走,不是只在内存里活一次。
   */
  const supplyMissing = (entries: { field: string; label: string; value: string }[]) => {
    if (entries.length === 0) return;
    const add = entries.map((e) => `${e.label}:${e.value}`).join("\n");
    const next = materialRaw.trim() ? `${materialRaw.trimEnd()}\n${add}` : add;
    setMaterialRaw(next);
    // 把新素材显式传下去(见 ensureDraft 注释:护栏,不依赖 setState 刷新时序)。
    extractMut.mutate(next);
  };

  const expandMut = useMutationWithToast({
    mutationFn: async () => {
      const id = await ensureDraft();
      await reporterService.startTask(id, { type: "KEYWORD_EXPAND" });
      return id;
    },
    errorToast: "拓词没发起来",
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["reporter-draft", id] });
    },
  });

  const planMut = useMutationWithToast({
    mutationFn: async () => {
      const id = await ensureDraft();
      await reporterService.startTask(id, { type: "PLAN" });
      return id;
    },
    errorToast: "规划没发起来",
    onSuccess: () => invalidate(),
  });

  const generateMut = useMutationWithToast({
    mutationFn: async () => {
      const id = await ensureDraft();
      await reporterService.startTask(id, { type: "GENERATE" });
      return id;
    },
    errorToast: "生成没发起来",
    onSuccess: () => invalidate(),
  });

  /* ── 换体裁 = 弃当前草稿号(体裁/产品建稿后锁定,如实重开) ── */
  const resetGenre = () => {
    setGenre(null);
    setSubType("");
    hydrated.current = false;
    if (draftId != null) setSp({}, { replace: true });
    qc.removeQueries({ queryKey: ["reporter-draft", draftId] });
  };

  /* ══════════ 渲染 ══════════ */

  // 未选体裁:先选(无预设直进/换体裁后)
  if (!genre) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <PageHeader
          backTo="/ai-jizhe"
          icon={Newspaper}
          title="新建文章"
          subtitle="先选体裁 —— 不同体裁整理的素材字段不同。"
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(Object.keys(GENRE_LABEL) as ReporterGenre[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => {
                genreTouched.current = true;
                setGenre(g);
              }}
              className="rounded-[calc(var(--radius)*1.5)] border bg-card px-4 py-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-md"
            >
              <div className="text-sm font-semibold">{GENRE_LABEL[g]}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const failedMsg = maskVendor(
    (extractTask?.status === "FAILED" && extractTask.errorMessage) ||
      (planTask?.status === "FAILED" && planTask.errorMessage) ||
      (draft?.status === "FAILED" && draft.errorMessage) ||
      null
  );

  return (
    <div className="w-full space-y-5">
      <PageHeader
        backTo="/ai-jizhe"
        icon={Newspaper}
        title="新建文章"
        subtitle="素材整理 → 规划确认 → 成稿,每步都能核对 AI 读懂了什么。"
        actions={
          <StepIndicator
            steps={STEPS}
            current={step}
            label="新闻舱写作流程"
            domainAttr="data-reporter-steps"
          />
        }
      />

      <SourceStrip
        tag={`体裁 · ${GENRE_LABEL[genre]}`}
        title={
          draftId != null
            ? `草稿 #${draftId}${productCode ? ` · 已挂产品` : ""}`
            : (reporterPreset?.sourceLabel ?? (reporterPreset ? "从场景卡进入" : "从体裁选择进入"))
        }
        meta="换体裁会重新开一篇(体裁决定整理的素材字段)"
        icon={<Newspaper className="h-5 w-5" />}
        accent
        domainAttr="data-reporter-source"
        action={
          <Button variant="ghost" size="sm" disabled={busy} onClick={resetGenre}>
            换体裁
          </Button>
        }
      />

      {/*
       * BPWB-2(2026-09-03):蓝图工作卡 —— 写法四格从素材框搬到这里展示分离,上送口径不变
       * (提交时 ensureDraft 用 withCraftMaterial() 把写法段重新拼回素材最前面再送)。
       * 来源条(SourceStrip)上面那句「来自追爆舱·「X」的写法」已经因为 buildReporterPreset
       * 不再带 sourceLabel 而不显示了,不会跟这张卡的头行说两遍同一句话。
       */}
      {reporterPreset?.formulaName && (
        <BlueprintHeroCard
          formulaName={reporterPreset.formulaName}
          sourcePlatform={reporterPreset.sourcePlatform}
          title={
            <>
              学「<b>{reporterPreset.formulaName}</b>」的写法
            </>
          }
          pledge={
            <>
              句式可以借,<b>事实不能借</b>:参考片的原话只按你选的借法用(引用要带出处、仿句只仿说法),
              <b>选题、事实、关键词必须是你自己的</b>。
            </>
          }
          structure={
            reporterPreset.blueprintCraft ? (
              <BlueprintCraftGrid craft={reporterPreset.blueprintCraft} />
            ) : undefined
          }
        />
      )}

      {/* 2026-10-10 §4.1「怎么借这条爆款」三选一(默认仿它的说法);逐句来自 C1 */}
      {blueprintId != null && (
        <BorrowPicker
          linesStatus={borrowLinesStatus}
          lines={borrowLines}
          mode={effectiveBorrowMode}
          onMode={onBorrowMode}
          quotes={effQuotes}
          onToggleQuote={(t) => setQuotePicks(togglePick(effQuotes, t))}
          attribution={attribution}
          onAttribution={setAttribution}
          styleLines={effStyle}
          onToggleStyle={(t) => setStylePicks(togglePick(effStyle, t))}
          disabled={busy}
        />
      )}
      {/* 编辑已有草稿(没有蓝图上下文):只读回显这篇借了什么,保存时原样回送 */}
      {blueprintId == null && hydratedBorrow && (
        <div
          data-testid="reporter-borrow-summary"
          className="space-y-1 rounded-lg border bg-card px-3.5 py-2.5 text-2xs text-muted-foreground"
        >
          <div>
            这篇借了一条爆款:
            <b className="text-foreground">{BORROW_MODE_META[hydratedBorrow.mode].label}</b>
          </div>
          {hydratedBorrow.quotes?.map((q) => (
            <div key={q.text}>
              引用「{q.text}」—— {q.attribution}
            </div>
          ))}
          {hydratedBorrow.styleLines?.map((t) => <div key={t}>仿句「{t}」</div>)}
        </div>
      )}

      {/* 2026-08-13:从追爆来的稿,把结构层原样摊开(素材框里那几行是内容层,两者分开看) */}
      {reporterPreset?.blueprintPayload && (
        <HandoffPreview payload={reporterPreset.blueprintPayload} />
      )}
      {/* 2026-08-14:内容层只读展示 —— 素材框仍只交写法,上送口径一个字不动 */}
      {reporterPreset?.blueprintBreakdown &&
        buildReferenceContent(reporterPreset.blueprintBreakdown) && (
          <ReferenceContentPanel
            content={buildReferenceContent(reporterPreset.blueprintBreakdown)}
            original={buildReferenceContent(reporterPreset.blueprintBreakdown)}
            readOnly
          />
        )}

      {/* R4 GEO 合规提示条:restricted 提醒 / forbidden 明示(导出预检另有确认档;不硬拦) */}
      {geoPolicy === "forbidden" && (
        <div
          className="rounded-lg border border-destructive/50 bg-destructive/5 px-3.5 py-2.5 text-xs text-destructive"
          data-reporter-geo-policy="forbidden"
        >
          你所在行业「{metaQ.data?.industryName}」属 <b>GEO 禁入区</b>
          :推广发文存在合规风险,可以写作,但导出交付前需要明确确认并会记录审计。
        </div>
      )}
      {geoPolicy === "restricted" && (
        <div
          className="rounded-lg border border-warning/50 bg-warning/5 px-3.5 py-2.5 text-xs text-warning"
          data-reporter-geo-policy="restricted"
        >
          你所在行业「{metaQ.data?.industryName}」属 <b>GEO 高难度区</b>
          :成稿会从严校验表述,建议素材尽量给足依据。
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          {/* ① 原始素材 */}
          <section className="rounded-[calc(var(--radius)+4px)] border bg-card p-4">
            <div className="mb-2.5 flex items-baseline gap-2">
              <h3 className="text-sm font-semibold">① 原始素材</h3>
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-2xs font-semibold text-destructive">
                必填
              </span>
              <span className="ml-auto text-2xs text-muted-foreground">
                整理{REPORTER_CREDITS_EXTRACT_APPROX} 积分/次(按实际用量),可反复跑
              </span>
            </div>
            {splitLegacyCraft(materialRaw).craft !== null && (
              <div
                data-testid="reporter-legacy-craft"
                className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-300/70 bg-amber-50 px-2.5 py-1.5 text-2xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
              >
                <span>
                  素材框开头那段「写法参考」是旧版自动带进来的参考片内容,不是你的素材 ——
                  留着重新生成,参考片的原话会被当成你家的事实。
                </span>
                <button
                  type="button"
                  disabled={busy}
                  className="font-semibold underline underline-offset-2"
                  onClick={() => setMaterialRaw(splitLegacyCraft(materialRaw).rest)}
                >
                  移除这段
                </button>
              </div>
            )}
            <Textarea
              value={materialRaw}
              onChange={(e) => setMaterialRaw(e.target.value)}
              disabled={busy}
              placeholder={
                reporterPreset?.formulaName
                  ? BLUEPRINT_MATERIAL_PLACEHOLDER
                  : DEFAULT_MATERIAL_PLACEHOLDER
              }
              className="min-h-[120px] text-sm leading-relaxed"
              data-reporter-material-input=""
            />

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-2xs text-muted-foreground">语气:</span>
              {TONE_ORDER.map((k) => (
                <button
                  key={k}
                  type="button"
                  disabled={busy}
                  onClick={() => setTone(k)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-2xs transition-colors",
                    tone === k
                      ? "border-primary bg-primary/10 font-semibold text-primary"
                      : "border-border text-muted-foreground hover:text-foreground"
                  )}
                >
                  {TONE_LABEL[k]}
                </button>
              ))}
              <label className="ml-auto inline-flex items-center gap-1.5 text-2xs text-muted-foreground">
                目标
                <input
                  type="number"
                  min={300}
                  max={5000}
                  step={100}
                  value={targetWords}
                  disabled={busy}
                  onChange={(e) => setTargetWords(Number(e.target.value) || 1200)}
                  className="w-20 rounded-md border border-border/60 bg-background px-2 py-1 text-xs"
                  aria-label="目标字数"
                />
                字 ±5%
              </label>
            </div>

            {genre === "BRAND_PROFILE" && (
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className="text-2xs text-muted-foreground">子类:</span>
                {BRAND_SUBTYPES.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    disabled={draftId != null}
                    title={draftId != null ? "建稿后子类锁定;换子类请换体裁重开一篇" : undefined}
                    onClick={() => setSubType(code === subType ? "" : code)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-2xs transition-colors",
                      subType === code
                        ? "border-primary bg-primary/10 font-semibold text-primary"
                        : "border-border text-muted-foreground hover:text-foreground",
                      draftId != null && "opacity-60"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* R2 关键词区:手填 + 一键拓词(候选按组勾选;生成时只带已勾选) */}
            <KeywordZone
              keywords={keywords}
              onChange={setKeywords}
              onExpand={() => expandMut.mutate(undefined)}
              expanding={expanding || expandMut.isPending}
              canExpand={!!materialRaw.trim()}
              disabled={busy}
            />

            {/* R1 知识库开关:默认开;检索不命中会在规划里明示,不装有 */}
            <label className="mt-2.5 inline-flex items-center gap-1.5 text-2xs text-muted-foreground">
              <input
                type="checkbox"
                checked={useKnowledge}
                disabled={busy}
                onChange={(e) => setUseKnowledge(e.target.checked)}
                data-reporter-use-knowledge=""
              />
              结合企业知识库写作(命中的资料会进依据地图,可核对来源)
            </label>

            {/* 产品归属(可选;建稿后锁定 —— 后端无改挂端点,不做假切换) */}
            <div className="mt-3">
              <select
                value={productCode}
                aria-label="产品归属(可选)"
                disabled={busy || draftId != null || productsQ.isLoading}
                title={draftId != null ? "建稿后产品归属锁定;要换产品请换体裁重开一篇" : undefined}
                onChange={(e) => setProductCode(e.target.value)}
                className="w-full rounded-md border border-border/60 bg-background/60 px-2 py-1.5 text-xs"
              >
                <option value="">
                  {productsQ.isLoading
                    ? "读取产品库…"
                    : products.length
                      ? "产品归属(可选)— 挂上后生成时带入产品知识"
                      : "产品库暂无在售产品"}
                </option>
                {products.map((n) => (
                  <option key={n.code} value={n.code}>
                    {n.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-3">
              <Button
                variant={material ? "outline" : "default"}
                disabled={busy || !materialRaw.trim() || extractMut.isPending}
                onClick={() => extractMut.mutate(undefined)}
                data-reporter-extract=""
              >
                {extracting || extractMut.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : material ? (
                  <RefreshCw className="mr-2 h-4 w-4" />
                ) : (
                  <Wand2 className="mr-2 h-4 w-4" />
                )}
                {material ? "重新整理" : "整理素材"}
              </Button>
            </div>
          </section>

          {/* ② AI 素材整理 */}
          {(material || extracting) && (
            <section className="rounded-[calc(var(--radius)+4px)] border bg-card p-4">
              <div className="mb-2.5 flex items-baseline gap-2">
                <h3 className="text-sm font-semibold">② AI 素材整理</h3>
                <span className="text-2xs text-muted-foreground">
                  四步流水线:提取 → 映射 → 查缺 → 补漏
                </span>
              </div>
              {extracting ? (
                <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  整理中 —— 逐字段找来源,素材里没有的不会编…
                </div>
              ) : (
                material && (
                  <MaterialTable
                    material={material}
                    supplying={extracting || extractMut.isPending}
                    onSupply={supplyMissing}
                  />
                )
              )}
            </section>
          )}

          {failedMsg && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/5 px-3.5 py-2.5 text-xs text-destructive">
              上一步失败:{failedMsg} —— 修改素材后可重试(失败不扣已完成步骤的结果)。
            </div>
          )}
          {/*
           * 0813 娜娜:「新闻仓失败,是否也可以有返回在任务里继续不用等待」。
           *
           * ★这件事**本来就成立**,只是页面从没说过:整理/规划/成稿都是服务端任务
           *   (状态挂在草稿上,这页 2.5s 轮询),草稿又能按 `?draftId=` 直接回来。
           *   切走不会打断,回来接着看。
           * ★所以这句话敢写「切走/刷新都不影响」—— 与长图那条**正好相反**:
           *   长图的读懂/排区块是同步 HTTP,切走真断,那边就绝不能照抄这句。
           *   同一句话在两个舱一真一假,判据是「这一步到底跑在谁那儿」。
           */}
          {busy && (
            <p
              data-testid="reporter-busy-can-leave"
              className="rounded-lg border border-dashed border-border bg-background/60 px-3.5 py-2.5 text-xs text-muted-foreground"
            >
              这一步在后台跑,<b className="font-medium text-foreground">切走或刷新都不影响</b> ——
              回「我的作品 · 我的草稿」点这篇就能接着看,不用守在这儿等。
            </p>
          )}
        </div>

        <PlanPanel
          plan={plan}
          planning={planning || planMut.isPending}
          generating={generating || generateMut.isPending}
          materialReady={!!material && !extracting}
          targetWords={targetWords}
          onPlan={() => planMut.mutate(undefined)}
          onGenerate={() => generateMut.mutate(undefined)}
        />
      </div>
    </div>
  );
}
